import React, { useState } from 'react';
import { ShieldCheck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { acceptInsuranceDeclaration } from '../services/api';

/**
 * Full-screen declaration gate shown to legacy drivers whose user record
 * doesn't yet have `insurance_confirmed=true`. Ticking + confirming posts
 * to POST /api/driver/insurance/declaration which sets it.
 *
 * Rendered inline (not a portal) so the top nav (Messages, existing jobs)
 * remains reachable behind the overlay.
 */
export default function InsuranceDeclarationModal({ onDeclared }) {
  const [agreed, setAgreed] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!agreed) return;
    setSaving(true);
    try {
      const { data } = await acceptInsuranceDeclaration();
      toast.success('Declaration accepted — you can now place bids.');
      onDeclared?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save your acceptance');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-40 bg-gray-900/60 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-8 px-4"
      data-testid="insurance-declaration-modal"
    >
      <div className="w-full max-w-2xl bg-white rounded-lg shadow-2xl border border-gray-200">
        <div className="p-6 border-b border-gray-200 flex items-start gap-3">
          <div className="w-12 h-12 rounded-full bg-indigo-50 flex items-center justify-center flex-shrink-0">
            <ShieldCheck className="w-6 h-6 text-indigo-700" strokeWidth={2} />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-bold font-['Inter'] text-gray-900">
              Driver Insurance Declaration
            </h2>
            <p className="text-sm text-gray-600 mt-1 leading-relaxed">
              To keep bidding on JustDeliver, please accept the Driver Insurance Declaration below.
              Existing jobs and messages remain accessible from the top navigation, but new bids are disabled until you accept.
            </p>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div className="p-4 bg-indigo-50/50 border border-indigo-100 rounded-md text-sm text-gray-800 leading-relaxed">
            I confirm that I hold, and will maintain, all insurance required by law to carry out paid delivery work in Ireland. I understand that I am solely responsible for ensuring my insurance remains valid and appropriate for the work I undertake through JustDeliver. I also understand that JustDeliver may request proof of insurance at any time, and failure to provide it may result in my account being suspended or terminated.
          </div>
          <p className="text-xs text-gray-500 leading-relaxed">
            JustDeliver is a technology platform connecting independent drivers with customers. Drivers are responsible for ensuring they have the appropriate insurance required for the services they provide.
          </p>
          <div className="flex items-start gap-2 pt-2">
            <Checkbox
              id="decl-agree"
              checked={agreed}
              onCheckedChange={(v) => setAgreed(!!v)}
              data-testid="declaration-agree-checkbox"
            />
            <Label htmlFor="decl-agree" className="text-sm font-medium cursor-pointer">
              I Agree
            </Label>
          </div>
          <div className="flex justify-end">
            <Button
              onClick={submit}
              disabled={!agreed || saving}
              className="bg-[#2E7D32] text-white hover:bg-[#1B5E20] rounded-md gap-2"
              data-testid="declaration-submit-btn"
            >
              {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : 'Accept & Continue'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
